/* eslint-disable @typescript-eslint/no-explicit-any */
import { Checkbox, FormError, Label } from '@kcftech/design-system';
import { MachineHealthEventType } from '@kcftech/prometheustypescriptapiclient';
import { GroupDropdown } from 'components/dropdown/GroupDropdown';
import { usePermissions } from 'contexts/PermissionsContext';
import { DeskNotificationTriggerTable } from 'pages/Settings/NotificationSubscriptions/DeskNotificationTriggerTable';
import { CreateNotificationSubscriptionFormInputProps } from 'pages/Settings/NotificationSubscriptions/form/types';
import { NotificationTriggerTableWrapper } from 'pages/Settings/NotificationSubscriptions/NotificationTriggerTable';
import { machineHealthEventTypeName } from 'pages/Settings/NotificationSubscriptions/shared';
import { useState } from 'react';
import { Controller, FieldError } from 'react-hook-form';

export function MachineHealthNotificationSubscriptionFormContent({
    form,
    location,
}: CreateNotificationSubscriptionFormInputProps) {
    const {
        control,
        formState: { errors, isSubmitted },
        watch,
        trigger,
    } = form;

    const watchedValues = watch();

    const [group, setGroup] = useState<{ Id: string; Name: string } | undefined>();

    const withFormValidate = (onChange: (e: unknown) => void) => (e: unknown) => {
        onChange(e);
        if (isSubmitted) {
            trigger();
        }
    };

    const { canViewMachineHealthNotificationSubscriptionCategory } = usePermissions();

    return (
        <>
            <Label label="Group" htmlFor="groupId" />
            <Controller
                control={control}
                name="machineHealth.groupId"
                render={({ field }) => (
                    <GroupDropdown
                        id="groupId"
                        value={group}
                        onChange={(newGroup) => {
                            field.onChange(newGroup.Id);
                            setGroup(newGroup);
                        }}
                        className="full-width mb-15"
                        disabled={!location}
                        title={!location ? 'Please select a location first' : undefined}
                        locationId={watchedValues.location?.Id ?? ''}
                        defaultValueText="All Systems"
                        allowSelectNone
                    />
                )}
            />
            <FormError message={errors.indicatorAlerts?.groupId?.message} />

            <NotificationTriggerTableWrapper className="mb-15">
                <DeskNotificationTriggerTable
                    rows={Object.values(MachineHealthEventType)
                        .filter(canViewMachineHealthNotificationSubscriptionCategory)
                        .map((category) => (
                            <tr key={category}>
                                <td>{machineHealthEventTypeName[category]}</td>
                                <td className="text-right">
                                    <Controller
                                        control={control}
                                        name={
                                            `machineHealth.notificationTriggers.${category}.IsEmailEnabled` as const
                                        }
                                        render={({ field: { value, onChange } }) => (
                                            <Checkbox
                                                label=""
                                                onChange={withFormValidate(onChange)}
                                                checked={value}
                                                title={`Toggle emails for ${machineHealthEventTypeName[
                                                    category
                                                ].toLowerCase()}s`}
                                            />
                                        )}
                                    />
                                </td>
                                <td className="text-right">
                                    <Controller
                                        control={control}
                                        name={
                                            `machineHealth.notificationTriggers.${category}.IsSdConnectPushEnabled` as const
                                        }
                                        render={({ field: { value, onChange } }) => (
                                            <Checkbox
                                                label=""
                                                disabled={
                                                    category ===
                                                    MachineHealthEventType.IssueActivity
                                                }
                                                onChange={withFormValidate(onChange)}
                                                checked={value}
                                                title={`${
                                                    category !==
                                                    MachineHealthEventType.IssueActivity
                                                        ? 'Toggle push notifications'
                                                        : 'Push notifications not available'
                                                } for ${machineHealthEventTypeName[
                                                    category
                                                ].toLowerCase()}`}
                                            />
                                        )}
                                    />
                                </td>
                            </tr>
                        ))}
                />

                {/* Need to cast to unknown and then any because react hook form doesn't contain the correct type for errors */}
                {((errors as unknown) as any)?.notificationTriggers?.map(
                    (errText: FieldError) => (
                        <FormError message={errText.message} key={errText.message} />
                    )
                )}
                <FormError
                    message={
                        ((errors as unknown) as any)?.notificationTriggerPhoneNumber
                            ?.message
                    }
                />
                <FormError
                    message={
                        ((errors as unknown) as any)?.notificationTriggerNonKCFUsers
                            ?.message
                    }
                />
            </NotificationTriggerTableWrapper>
        </>
    );
}
